import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Spinner } from "@/components/ui/spinner";
import { MediaFrame, MediaSheet, ratioClass } from "@/components/content-programs/media-tile";
import { storageService } from "@/services/storage.service";
import { addProgramSample, deleteProgramSample, type ProgramSample } from "@/services/contentProgram.service";
import {
  getAcceptMimeList,
  getUploadVibe,
  validateContentFileWithConfig,
  type ContentProgramConfig,
  type MetaAspectRatio,
} from "@/config/contentProgramUpload";
import { cn } from "@/lib/utils";

const MAX_SAMPLES = 4;

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function helperText(config: ContentProgramConfig): string {
  const vibe = getUploadVibe(config.allowedMediaTypes);
  const noun = vibe === "video" ? "videos" : vibe === "image" ? "images" : "images or videos";
  return `Up to ${MAX_SAMPLES} ${noun} affiliates see as a reference when they submit`;
}

type PersistedProps = {
  mode: "persisted";
  programId: string;
  samples: ProgramSample[];
  contentConfig: ContentProgramConfig;
};

type StagingProps = {
  mode: "staging";
  stagedFiles: File[];
  onStagedFilesChange: (files: File[]) => void;
  contentConfig: ContentProgramConfig;
};

type Props = PersistedProps | StagingProps;

/** The remove control on a sample: shown on hover and on focus. */
function RemoveButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Remove sample"
      title="Remove sample"
      className="absolute right-1.5 top-1.5 z-10 inline-flex size-7 items-center justify-center rounded-md bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/70 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100 disabled:opacity-50"
    >
      <IconTrash size={16} stroke={1.75} />
    </button>
  );
}

/** A sample the brand uploaded; the frame reads the file's own proportions. */
function SampleTile({
  src,
  isVideo,
  ratio,
  onRemove,
  removing,
}: {
  src: string | null;
  isVideo: boolean;
  ratio: MetaAspectRatio;
  onRemove: () => void;
  removing?: boolean;
}) {
  return (
    <span className="group relative block">
      <MediaFrame ratio={ratio} poster={isVideo ? null : src} isVideo={false}>
        {isVideo && src && (
          <video src={src} muted playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <RemoveButton onClick={onRemove} disabled={removing} />
      </MediaFrame>
    </span>
  );
}

function StagedFileTile({ file, ratio, onRemove }: { file: File; ratio: MetaAspectRatio; onRemove: () => void }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return <SampleTile src={previewUrl} isVideo={file.type.startsWith("video/")} ratio={ratio} onRemove={onRemove} />;
}

export default function SampleContentManager(props: Props) {
  const { contentConfig } = props;
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);

  const isPersisted = props.mode === "persisted";
  const count = isPersisted ? props.samples.length : props.stagedFiles.length;
  const acceptMimes = getAcceptMimeList(contentConfig);
  // Samples are drawn at the program's first allowed placement.
  const ratio: MetaAspectRatio = contentConfig.allowedAspectRatios[0] ?? "9:16";

  const invalidate = () => {
    if (!isPersisted) return;
    void queryClient.invalidateQueries({ queryKey: ["brand-content-program", props.programId] });
  };

  const removeMut = useMutation({
    mutationFn: (sampleId: string) => {
      if (!isPersisted) throw new Error("Invalid mode");
      return deleteProgramSample(props.programId, sampleId);
    },
    onSuccess: () => {
      toast.success("Sample removed");
      invalidate();
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    if (count >= MAX_SAMPLES) {
      toast.error(`Up to ${MAX_SAMPLES} samples`);
      return;
    }
    setValidating(true);
    try {
      const chk = await validateContentFileWithConfig(file, contentConfig);
      if (!chk.ok) {
        toast.error(chk.error);
        return;
      }
      if (props.mode === "staging") {
        props.onStagedFilesChange([...props.stagedFiles, file]);
        return;
      }
      setUploading(true);
      try {
        const mediaUrl = await storageService.uploadProgramSampleMedia(file);
        await addProgramSample(props.programId, {
          mediaUrl,
          mediaType: chk.mediaType,
          thumbnailUrl: chk.mediaType === "IMAGE" ? mediaUrl : undefined,
          sortOrder: props.samples.length,
        });
        toast.success("Sample added");
        invalidate();
      } catch (e) {
        toast.error(errMessage(e));
      } finally {
        setUploading(false);
      }
    } finally {
      setValidating(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeStaged = (index: number) => {
    if (props.mode !== "staging") return;
    props.onStagedFilesChange(props.stagedFiles.filter((_, i) => i !== index));
  };

  const busy = uploading || validating;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{helperText(contentConfig)}</p>

      <MediaSheet ratio={ratio}>
        {isPersisted
          ? props.samples.map((sample) => (
              <SampleTile
                key={sample.id}
                src={sample.mediaUrl}
                isVideo={sample.mediaType === "VIDEO"}
                ratio={ratio}
                onRemove={() => removeMut.mutate(sample.id)}
                removing={removeMut.isPending}
              />
            ))
          : props.stagedFiles.map((file, index) => (
              <StagedFileTile
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                ratio={ratio}
                onRemove={() => removeStaged(index)}
              />
            ))}

        {count < MAX_SAMPLES && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
              ratioClass(ratio)
            )}
          >
            {busy ? (
              <Spinner size="sm" />
            ) : (
              <>
                <IconPlus size={16} stroke={1.75} aria-hidden />
                Add sample
              </>
            )}
          </button>
        )}
      </MediaSheet>

      <input
        ref={inputRef}
        type="file"
        accept={acceptMimes}
        className="hidden"
        onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
